import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useCarLeasingApi } from "../../http/carLeasings/api";
import { UpdateCarLeasing } from "../../http/carLeasings/types";
import ModalDialog from "../ui/ModalDialog";
import CarLeasingForm, { CarLeasingFormBody } from "./CarLeasingForm";

type EditCarLeasingDialogProps = {
  carId: string;
  onClose: (success?: boolean) => void;
};

const EditCarLeasingDialog: FC<EditCarLeasingDialogProps> = ({ carId, onClose }: EditCarLeasingDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const carLeasingApi = useCarLeasingApi();

  const carLeasingSwr = carLeasingApi.getAdminCarLeasing(carId, aborter.current.signal);
  const [carLeasing, isInitialized] = useDataInitializer(carLeasingSwr.data, carLeasingSwr.error);

  useEffect(() => {
    carLeasingSwr.trigger();
  }, [carId]);

  useEffect(() => {
    carLeasingSwr.error && dispatch(showErrorNotification(i18n, carLeasingSwr.error));
  }, [carLeasingSwr.error]);

  const onSubmit = async (formData: CarLeasingFormBody): Promise<void> => {
    if (carLeasing) {
      const body: UpdateCarLeasing = {
        leasingCompanyId: formData.company?.id!,
        contractDate: formData.contractDate,
        contractDetails: formData.details!,
        expirationDate: formData.expirationDate,
        currentVersionNumber: carLeasing?.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await carLeasingApi.updateAdminCarLeasing(carLeasing.car.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <ModalDialog onClose={onClose} title={i18n.t.carLeasings.edit}>
      <CarLeasingForm
        onSubmit={onSubmit}
        isDisabled={!carLeasing || !userSettings.permissions.carLeasings.update || isSubmitting}
        isLoading={!isInitialized}
        carLeasing={carLeasing}
        carId={carId}
      />
    </ModalDialog>
  );
};

export default EditCarLeasingDialog;
